import React, { useState } from 'react';
import { 
  Box, 
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Heading,
  Circle,
  Flex,
  Button,
  useColorModeValue,
  SimpleGrid,
  Link,
  Divider,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Progress
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  Activity, 
  type LucideIcon, 
  BrainIcon, 
  UsersIcon, 
  BuildingIcon,
  TrendingUpIcon,
  ShieldIcon,
  HeartIcon,
  ClockIcon,
  CheckCircleIcon,
  CalendarIcon,
  MessageCircleIcon,
  BarChart2Icon,
  SettingsIcon,
  NetworkIcon,
  ZapIcon,
  StarIcon,
  InfoIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from 'lucide-react';
import MobileResilienceActivityDetail from './MobileResilienceActivityDetail';

type DomainType = 'AQ' | 'CQ' | 'WQ' | 'ALL';

type MetricType = {
  label: string;
  value: number;
  goal: number;
  icon: LucideIcon;
  color: string;
  progress: number;
  domain: DomainType;
  description?: string;
  trend?: number;
  insights?: string[];
}

type ActionType = {
  name: string;
  icon: LucideIcon;
  color: string;
  activityId?: string;
  onClick?: () => void;
  link?: string;
  domain: DomainType;
}

type HighlightType = {
  label: string;
  value: string;
  icon: LucideIcon;
  progress: number;
  goal: number;
  domain: DomainType;
}

type GoalType = {
  current: number;
  target: number;
  label: string;
  domain: DomainType;
}

type DomainScoreType = {
  domain: DomainType;
  score: number;
  trend: string;
  label: string;
  title: string;
  icon: LucideIcon;
  color: string;
  insight: string;
}

type Props = {
  onNavigate: (tab: string, domain?: DomainType) => void;
  metrics: MetricType[];
  highlights: HighlightType[];
  goals: GoalType[];
  actions: ActionType[];
  domainScores: DomainScoreType[];
}

const methodistColors = {
  blue: '#0066CC',
  navy: '#002D72',
  green: '#38A169',
  purple: '#805AD5',
};

const domainColors = {
  AQ: methodistColors.green,
  CQ: methodistColors.purple,
  WQ: methodistColors.blue,
};

const domainIcons = {
  AQ: BrainIcon,
  CQ: UsersIcon,
  WQ: BuildingIcon,
  ALL: StarIcon
};

const domainLabels = {
  AQ: 'Adaptive Quotient',
  CQ: 'Cultural Quotient',
  WQ: 'Wellness Quotient'
};

const UnifiedHomePage = ({ 
  onNavigate,
  metrics,
  highlights,
  goals,
  actions,
  domainScores
}: Props) => {
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [activeDomain, setActiveDomain] = useState<DomainType>('ALL');
  const [selectedMetric, setSelectedMetric] = useState<MetricType | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Colors
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.500', 'gray.500');
  
  const handleActionClick = (action: ActionType) => {
    if (action.activityId) {
      setSelectedActivity(action.activityId);
    } else if (action.link) {
      onNavigate(action.link, action.domain);
    } else if (action.onClick) {
      action.onClick();
    }
  };

  const handleBackToHome = () => {
    setSelectedActivity(null);
  };

  const handleMetricClick = (metric: MetricType) => {
    setSelectedMetric(metric);
    onOpen();
  };

  // If an activity is selected, show the activity detail view
  if (selectedActivity) {
    return (
      <MobileResilienceActivityDetail 
        activityId={selectedActivity} 
        onBack={handleBackToHome} 
      />
    );
  }

  // Filter metrics, highlights, goals, and actions by domain
  const filteredMetrics = metrics.filter(metric => 
    activeDomain === 'ALL' || metric.domain === activeDomain
  );

  const filteredHighlights = highlights.filter(highlight => 
    activeDomain === 'ALL' || highlight.domain === activeDomain
  );

  const filteredGoals = goals.filter(goal => 
    activeDomain === 'ALL' || goal.domain === activeDomain
  );

  // For actions, include both domain-specific actions and those marked with 'ALL'
  const filteredActions = actions.filter(action => 
    activeDomain === 'ALL' || action.domain === activeDomain || action.domain === 'ALL'
  );

  // Get the active domain score
  const activeDomainScore = domainScores.find(ds => ds.domain === activeDomain);
  
  return (
    <Box>
      {/* Unified Header with Overall Wellness Score */}
      <Box 
        bg={methodistColors.blue} 
        color="white" 
        pt={12} 
        pb={4} 
        px={4}
        borderBottomRadius="2xl"
      >
        <HStack spacing={2} mb={2}>
          <Icon as={StarIcon} boxSize={4} />
          <Text fontSize="sm" fontWeight="medium">
            SentientIQ Wellness Platform
          </Text>
        </HStack>
        
        <HStack justify="space-between" align="flex-end" mb={4}>
          <VStack align="flex-start" spacing={0}>
            <Text fontSize="4xl" fontWeight="bold" lineHeight="1">
              {Math.round((domainScores.reduce((sum, ds) => sum + ds.score, 0) / domainScores.length))}
            </Text>
            <Text fontSize="sm" opacity={0.9}>
              Overall Wellness Score
            </Text>
          </VStack>
          <Box
            bg="green.400"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
          >
            +{Math.round((domainScores.reduce((sum, ds) => sum + parseInt(ds.trend.replace('+', '')), 0) / domainScores.length))}
          </Box>
        </HStack>

        {/* Domain Score Pills */}
        <HStack spacing={2} overflowX="auto" pb={2}>
          {domainScores.map((domainScore, idx) => (
            <Box 
              key={idx}
              bg={domainScore.domain === activeDomain ? 'white' : 'whiteAlpha.300'}
              color={domainScore.domain === activeDomain ? domainScore.color : 'white'}
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              fontWeight="medium"
              onClick={() => setActiveDomain(domainScore.domain)}
              cursor="pointer"
              display="flex"
              alignItems="center"
            >
              <Icon as={domainScore.icon} boxSize={3} mr={1} />
              <Text>{domainScore.score} {domainScore.label}</Text>
            </Box>
          ))}
        </HStack>
      </Box>

      <Box px={4} mt={4}>
        {/* Domain-Specific Insight */}
        {activeDomainScore && (
          <Box 
            mb={6} 
            bg={`${domainColors[activeDomainScore.domain]}10`} 
            p={3} 
            borderRadius="lg"
            fontSize="xs"
            color="gray.700"
          >
            <HStack mb={1}>
              <Icon as={activeDomainScore.icon} color={domainColors[activeDomainScore.domain]} boxSize={3} />
              <Text fontWeight="medium" color={domainColors[activeDomainScore.domain]}>
                {activeDomainScore.title} Insight
              </Text>
            </HStack>
            {activeDomainScore.insight}
          </Box>
        )}

        {/* Domain Metrics Section - Interactive and Reduced */}
        <Box mb={6}>
          <HStack mb={2} justify="space-between">
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              KEY METRICS
            </Text>
            <Text 
              fontSize="xs" 
              color="blue.500" 
              fontWeight="medium"
              cursor="pointer"
              onClick={() => onNavigate('metrics', activeDomain === 'ALL' ? 'AQ' : activeDomain)}
            >
              View All
            </Text>
          </HStack>
          
          {/* Reduced metrics - show only top 2 metrics with enhanced interactivity */}
          <SimpleGrid columns={2} spacing={3}>
            {filteredMetrics
              .sort((a, b) => (b.value / b.goal) - (a.value / a.goal)) // Sort by completion percentage
              .slice(0, 2) // Only show top 2 metrics
              .map((metric, idx) => {
                const domainColor = metric.domain === 'AQ' ? 
                  methodistColors.green : 
                  metric.domain === 'CQ' ? 
                  methodistColors.purple : 
                  methodistColors.blue;
                
                return (
                  <Box 
                    key={idx} 
                    bg="white" 
                    p={3} 
                    borderRadius="md" 
                    shadow="sm"
                    position="relative"
                    overflow="hidden"
                    onClick={() => handleMetricClick(metric)}
                    cursor="pointer"
                    _hover={{ transform: 'translateY(-2px)', shadow: 'md' }}
                    transition="all 0.2s"
                  >
                    <Box 
                      position="absolute" 
                      top={0} 
                      left={0} 
                      right={0} 
                      h="4px" 
                      bg={`${domainColor}15`}
                    >
                      <Box 
                        h="100%" 
                        w={`${(metric.value / metric.goal) * 100}%`} 
                        bg={domainColor}
                      />
                    </Box>
                    
                    <HStack spacing={2} mt={1} justify="space-between">
                      <HStack>
                        <Icon 
                          as={metric.icon} 
                          color={domainColor} 
                          boxSize={4}
                        />
                        <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
                          {metric.label}
                        </Text>
                      </HStack>
                      <Icon as={InfoIcon} boxSize={3} color="gray.400" />
                    </HStack>
                    
                    <HStack justify="space-between" align="flex-end" mt={2}>
                      <Text 
                        fontSize="xl" 
                        fontWeight="bold"
                        color={domainColor}
                      >
                        {metric.value}
                        <Text as="span" fontSize="xs" color="gray.500" ml={1}>
                          /{metric.goal}
                        </Text>
                      </Text>
                      
                      {metric.trend && (
                        <HStack spacing={0} align="center">
                          <Icon 
                            as={metric.trend > 0 ? ArrowUpIcon : ArrowDownIcon} 
                            color={metric.trend > 0 ? 'green.500' : 'red.500'}
                            boxSize={3}
                          />
                          <Text 
                            fontSize="xs" 
                            color={metric.trend > 0 ? 'green.500' : 'red.500'}
                          >
                            {Math.abs(metric.trend)}%
                          </Text>
                        </HStack>
                      )}
                    </HStack>
                  </Box>
                );
              })}
          </SimpleGrid>
        </Box>

        {/* Enhanced Goals Section */}
        <Box mb={6}>
          <HStack mb={2} justify="space-between">
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              CURRENT GOALS
            </Text>
          </HStack>
          
          {/* Show one goal per domain in a more visual way */}
          <VStack spacing={3} align="stretch">
            {Object.entries({
              'AQ': filteredGoals.find(g => g.domain === 'AQ'),
              'CQ': filteredGoals.find(g => g.domain === 'CQ'),
              'WQ': filteredGoals.find(g => g.domain === 'WQ')
            })
            .filter(([_, goal]) => goal) // Filter out undefined goals
            .map(([domain, goal]) => (
              <Box 
                key={domain}
                p={3} 
                bg="white" 
                borderRadius="md" 
                shadow="sm"
                borderLeftWidth="4px"
                borderLeftColor={domain === 'AQ' ? 'green.500' : domain === 'CQ' ? 'purple.500' : 'blue.500'}
              >
                <HStack justify="space-between" align="center">
                  <VStack align="start" spacing={0}>
                    <HStack spacing={2}>
                      <Icon 
                        as={domainIcons[domain as DomainType]} 
                        color={domain === 'AQ' ? 'green.500' : domain === 'CQ' ? 'purple.500' : 'blue.500'}
                        boxSize={3}
                      />
                      <Text fontSize="xs" color="gray.600">
                        {domainLabels[domain as DomainType]}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="medium">
                      {goal?.label}
                    </Text>
                  </VStack>
                  
                  <HStack spacing={1} align="baseline">
                    <Text fontSize="lg" fontWeight="bold">
                      {goal?.current}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      /{goal?.target}
                    </Text>
                  </HStack>
                </HStack>
                
                {/* Progress bar */}
                <Box mt={2} w="100%" h="6px" bg="gray.100" borderRadius="full">
                  <Box 
                    h="100%" 
                    w={`${(goal?.current || 0) / (goal?.target || 1) * 100}%`} 
                    bg={domain === 'AQ' ? 'green.500' : domain === 'CQ' ? 'purple.500' : 'blue.500'}
                    borderRadius="full"
                  />
                </Box>
              </Box>
            ))}
          </VStack>
        </Box>

        {/* Key Metrics Grid */}
        {filteredMetrics.length > 0 && (
          <Box mb={6}>
            <Text fontSize="xs" color="gray.600" mb={2}>
              Key Metrics
            </Text>
            <SimpleGrid columns={3} spacing={3}>
              {filteredMetrics.map((metric, idx) => (
                <Box
                  key={idx}
                  bg="white"
                  p={3}
                  borderRadius="lg"
                  shadow="sm"
                  borderWidth={1}
                  borderColor="gray.100"
                >
                  <HStack spacing={1}>
                    <Icon 
                      as={metric.icon} 
                      color={metric.color} 
                      boxSize={3} 
                    />
                    <Icon 
                      as={domainIcons[metric.domain]} 
                      color={domainColors[metric.domain]} 
                      boxSize={2} 
                    />
                  </HStack>
                  <HStack spacing={1} align="baseline">
                    <Text fontSize="lg" fontWeight="semibold">
                      {metric.value}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      /{metric.goal}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    {metric.label}
                  </Text>
                  <Box
                    w="full"
                    h="2px"
                    bg="gray.100"
                    overflow="hidden"
                    borderRadius="full"
                  >
                    <Box
                      w={`${(metric.value / metric.goal) * 100}%`}
                      h="full"
                      bg={metric.color}
                    />
                  </Box>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        )}

        {/* Highlights Scroll */}
        {filteredHighlights.length > 0 && (
          <Box mb={6}>
            <Text fontSize="xs" color="gray.600" mb={2}>
              Recent Highlights
            </Text>
            <HStack spacing={3} overflowX="auto" pb={2}>
              {filteredHighlights.map((highlight, idx) => (
                <Box
                  key={idx}
                  bg="white"
                  p={3}
                  borderRadius="lg"
                  shadow="sm"
                  minW="105px"
                  borderWidth={1}
                  borderColor="gray.100"
                >
                  <HStack spacing={1}>
                    <Icon 
                      as={highlight.icon} 
                      color={domainColors[highlight.domain]} 
                      boxSize={3} 
                    />
                    <Icon 
                      as={domainIcons[highlight.domain]} 
                      color={domainColors[highlight.domain]} 
                      boxSize={2} 
                    />
                  </HStack>
                  <Text fontSize="lg" fontWeight="semibold">
                    {highlight.value}
                  </Text>
                  <Text fontSize="xs" color="gray.600" mb={1}>
                    {highlight.label}
                  </Text>
                  <Box
                    w="full"
                    h="2px"
                    bg="gray.100"
                    overflow="hidden"
                    borderRadius="full"
                  >
                    <Box
                      w={`${(highlight.progress / highlight.goal) * 100}%`}
                      h="full"
                      bg={domainColors[highlight.domain]}
                    />
                  </Box>
                </Box>
              ))}
            </HStack>
          </Box>
        )}

        {/* Quick Actions */}
        <Box mb={6}>
          <HStack mb={2} justify="space-between">
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              QUICK ACTIONS
            </Text>
          </HStack>
          <SimpleGrid columns={2} spacing={3}>
            {/* Group actions by primary and secondary */}
            {filteredActions
              .sort((a, b) => {
                // JIMI Assistant should always appear first
                if (a.domain === 'ALL') return -1;
                if (b.domain === 'ALL') return 1;
                // Then primary actions (non-metrics)
                if (!a.name.includes('Metrics') && b.name.includes('Metrics')) return -1;
                if (a.name.includes('Metrics') && !b.name.includes('Metrics')) return 1;
                return 0;
              })
              .slice(0, 4) // Limit to 4 actions to reduce cognitive load
              .map((action, index) => (
                <Box
                  key={index}
                  p={3}
                  bg="white"
                  shadow="sm"
                  borderRadius="md"
                  onClick={() => handleActionClick(action)}
                  cursor="pointer"
                  role="button"
                  _hover={{ bg: 'gray.50' }}
                >
                  <HStack spacing={3}>
                    <Box
                      p={2}
                      borderRadius="md"
                      bg={action.domain === 'ALL' ? 'purple.50' : 
                         action.name.includes('Metrics') ? 'green.50' : 'blue.50'}
                      color={action.domain === 'ALL' ? 'purple.500' : 
                           action.name.includes('Metrics') ? 'green.500' : 'blue.500'}
                    >
                      <Icon as={action.icon} boxSize={4} />
                    </Box>
                    <Text fontSize="sm" fontWeight="medium">
                      {action.name}
                    </Text>
                  </HStack>
                </Box>
              ))}
          </SimpleGrid>
        </Box>

        {/* Streak Achievement */}
        <Box mb={6}>
          <Text fontSize="xs" color="gray.600" mb={2}>
            Wellness Streak
          </Text>
          <Box
            bg="white"
            p={3}
            borderRadius="lg"
            shadow="sm"
            borderWidth={1}
            borderColor="gray.100"
          >
            <HStack justify="space-between" align="center">
              <VStack align="start" spacing={0}>
                <Text fontSize="lg" fontWeight="semibold">
                  14 Days
                </Text>
                <Text fontSize="xs" color="gray.600">
                  Current Streak
                </Text>
              </VStack>
              <HStack>
                {[1, 2, 3, 4, 5, 6, 7].map((day) => (
                  <Circle 
                    key={day} 
                    size="16px" 
                    bg={day <= 5 ? "green.100" : "gray.100"}
                    color={day <= 5 ? "green.500" : "gray.400"}
                  >
                    <Icon as={CheckCircleIcon} boxSize={2} />
                  </Circle>
                ))}
              </HStack>
            </HStack>
          </Box>
        </Box>
      </Box>

      {/* Metric Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalHeader>
            {selectedMetric && (
              <HStack>
                <Icon 
                  as={selectedMetric.icon} 
                  color={selectedMetric.domain === 'AQ' ? 
                    methodistColors.green : 
                    selectedMetric.domain === 'CQ' ? 
                    methodistColors.purple : 
                    methodistColors.blue} 
                  boxSize={5}
                />
                <Text>{selectedMetric.label}</Text>
              </HStack>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedMetric && (
              <VStack align="stretch" spacing={4}>
                <Box bg="gray.50" p={4} borderRadius="md">
                  <Text fontSize="sm" color="gray.600" mb={1}>Current Progress</Text>
                  <HStack justify="space-between" mb={2}>
                    <Text fontSize="2xl" fontWeight="bold" color={selectedMetric.domain === 'AQ' ? 
                      methodistColors.green : 
                      selectedMetric.domain === 'CQ' ? 
                      methodistColors.purple : 
                      methodistColors.blue}>
                      {selectedMetric.value}
                    </Text>
                    <Text fontSize="md" color="gray.500">Goal: {selectedMetric.goal}</Text>
                  </HStack>
                  <Progress 
                    value={(selectedMetric.value / selectedMetric.goal) * 100} 
                    size="sm" 
                    colorScheme={selectedMetric.domain === 'AQ' ? 
                      'green' : 
                      selectedMetric.domain === 'CQ' ? 
                      'purple' : 
                      'blue'} 
                    borderRadius="full" 
                  />
                </Box>
                
                {selectedMetric.description && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={1}>About This Metric</Text>
                    <Text fontSize="sm" color="gray.600">{selectedMetric.description}</Text>
                  </Box>
                )}
                
                {selectedMetric.insights && selectedMetric.insights.length > 0 && (
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" mb={2}>Insights</Text>
                    <VStack align="stretch" spacing={2}>
                      {selectedMetric.insights.map((insight, idx) => (
                        <HStack key={idx} spacing={2} bg="gray.50" p={2} borderRadius="md">
                          <Icon as={TrendingUpIcon} color="blue.500" boxSize={4} />
                          <Text fontSize="sm">{insight}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button 
              colorScheme="blue" 
              size="sm" 
              width="full"
              onClick={() => {
                onClose();
                onNavigate('metrics', selectedMetric?.domain || activeDomain);
              }}
            >
              View Detailed Analysis
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default UnifiedHomePage;
